import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { medicationAdministrationService, MedicationAdministration } from '../../../lib/services.ipd';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

interface MedicationTabProps {
  admissionId: string;
}

export default function MedicationTab({ admissionId }: MedicationTabProps) {
  const [medications, setMedications] = useState<MedicationAdministration[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<{
    medicine_name: string;
    dose: string;
    route: 'oral' | 'iv' | 'im' | 'subcutaneous' | 'topical';
    frequency: string;
    remarks: string;
  }>();

  useEffect(() => {
    loadMedications();
  }, [admissionId]);

  const loadMedications = async () => {
    try {
      const data = await medicationAdministrationService.getByAdmission(admissionId);
      setMedications(data);
    } catch (error) {
      console.error('Failed to load medications:', error);
      toast.error('Failed to load medication records');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      await medicationAdministrationService.markAsGiven({
        admission_id: admissionId,
        medicine_name: data.medicine_name,
        dose: data.dose,
        route: data.route,
        frequency: data.frequency,
        given_at: new Date().toISOString(),
        remarks: data.remarks || undefined,
        nurse_id: '',
      });
      toast.success('Medication marked as given');
      reset();
      setShowAddForm(false);
      loadMedications();
    } catch (error) {
      console.error('Failed to record medication:', error);
      toast.error('Failed to record medication');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Medication Administration Record</h3>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            reset();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Mark as Given
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medicine Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('medicine_name', { required: 'Medicine name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Paracetamol"
              />
              {errors.medicine_name && (
                <p className="text-sm text-red-600 mt-1">{errors.medicine_name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dose <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('dose', { required: 'Dose is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 500mg"
              />
              {errors.dose && <p className="text-sm text-red-600 mt-1">{errors.dose.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Route <span className="text-red-500">*</span>
              </label>
              <select
                {...register('route', { required: 'Route is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select route</option>
                <option value="oral">Oral</option>
                <option value="iv">IV</option>
                <option value="im">IM</option>
                <option value="subcutaneous">Subcutaneous</option>
                <option value="topical">Topical</option>
              </select>
              {errors.route && <p className="text-sm text-red-600 mt-1">{errors.route.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('frequency', { required: 'Frequency is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., TID, BID, QID"
              />
              {errors.frequency && (
                <p className="text-sm text-red-600 mt-1">{errors.frequency.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Remarks
              </label>
              <input
                type="text"
                {...register('remarks')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Optional remarks"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Record Administration
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time Given</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medicine</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dose</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Route</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {medications.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No medications administered yet
                </td>
              </tr>
            ) : (
              medications.map((med) => (
                <tr key={med.id}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(med.given_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {med.medicine_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{med.dose}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 capitalize">{med.route}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{med.frequency}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{med.remarks || '-'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
